import { inject, Injectable } from '@angular/core';
import { isEqual } from 'lodash-es';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { WithFieldValue } from '../db/firestore.models';
import { objectToArr } from '../utils';
import { SettingsDbService, shippingBalancerByZoneFirestoreConverter } from './settings-db.service';
import { AdminSettings, BankInformationSettings, BannersSettings, Faq, GeneralSettings, NotificationsSettings, PrintSettingsConfig, RecommendedProductsForOccupation, ReferrersSettings, ShippingBalancerByZone, ShippingSettings } from './settings-models';
import { ServerSettings } from './settings-models/server-settings.models';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  private settingsDbService = inject(SettingsDbService);

  /**
   * Gets admin settings document in real time.
   * @returns Observable with admin settings.
   */
  getAdminSettings$(): Observable<AdminSettings> {
    return this.settingsDbService.get$<AdminSettings>('admin');
  }

  /**
   * Gets bank information settings in real time.
   * @returns Observable with bank information settings.
   */
  getBankInformation$(): Observable<BankInformationSettings> {
    return this.settingsDbService.get$<BankInformationSettings>('bankInformation');
  }

  /**
   * Gets banners settings in real time.
   * @returns Observable with banners configuration.
   */
  getBanners$(): Observable<BannersSettings> {
    return this.settingsDbService.get$<BannersSettings>('banners');
  }

  /**
   * Gets FAQs ordered by creation date.
   * @returns Observable with FAQ list.
   */
  getFaqs$(): Observable<Faq[]> {
    return this.settingsDbService.getWhere$<Faq>({ orderBy: [{ createdAt: 'asc' }], limit: 500 }, { subPath: 'faqs/items' });
  }

  /**
   * Gets general settings document in real time.
   * @returns Observable with general settings.
   */
  getGeneralSettings$(): Observable<GeneralSettings> {
    return this.settingsDbService.get$<GeneralSettings>('general');
  }

  /**
   * Gets notifications settings document in real time.
   * @returns Observable with notifications settings.
   */
  getNotificationsSettings$(): Observable<NotificationsSettings> {
    return this.settingsDbService.get$<NotificationsSettings>('notifications');
  }

  /**
   * Gets the in-app WhatsApp phone number when that channel is enabled.
   * @returns Observable with the configured phone number or null.
   */
  getWhatsappPhoneNumberForInApp$(): Observable<string | null> {
    return this.getNotificationsSettings$().pipe(
      map(settings => settings?.whatsappEnabledInApp && settings?.whatsappPhoneNumber
        ? settings.whatsappPhoneNumber
        : null)
    );
  }

  /**
   * Gets print settings document in real time.
   * @returns Observable with print settings.
   */
  getPrintSettings$(): Observable<PrintSettingsConfig> {
    return this.settingsDbService.get$<PrintSettingsConfig>('printSettings');
  }

  /**
   * Gets recommended product IDs for a given occupation.
   * @param occupation Occupation identifier.
   * @returns Observable with product ID list.
   */
  getRecommendedProductIdsByOccupation$(occupation: string): Observable<string[]> {
    return this.settingsDbService.get$<RecommendedProductsForOccupation>(occupation, { subPath: 'recommendedProducts/occupations' })
      .pipe(
        map(data => data?.productIds || [])
      );
  }

  /**
   * Gets referrers settings document in real time.
   * @returns Observable with referrers settings.
   */
  getReferrersSettings$(): Observable<ReferrersSettings> {
    return this.settingsDbService.get$<ReferrersSettings>('referrers');
  }

  /**
   * Gets server settings document in real time.
   * @returns Observable with server settings.
   */
  getServerSettings$(): Observable<ServerSettings> {
    return this.settingsDbService.get$<ServerSettings>('server');
  }

  /**
   * Gets shipping settings in real time, filtering repeated emissions with deep comparison.
   * @returns Observable with shipping settings.
   */
  getShippingSettings$(): Observable<ShippingSettings> {
    return this.settingsDbService.get$<ShippingSettings>('shippingSettings').pipe(
      distinctUntilChanged(isEqual)
    );
  }

  /**
   * Gets shipping settings once and normalizes object maps into arrays.
   * @returns Promise with normalized shipping settings.
   */
  getShippingSettings(): Promise<ShippingSettings> {
    return this.settingsDbService.get<ShippingSettings>('shippingSettings')
      .then(data => {
        const {
          freeWeightLimit,
          weightPrices,
          priceRanges,
          zoneWeightPrices,
          zoneFreeCases,
          zoneShippingInfo,
          shippingTypeRestrictions,
          extra_economic,
          extra_24_48,
          extra_24,
          extra_urgent,
          available,
          deliveryDaysRanges
        } = data;
        const shippingSettings = {
          extra_economic, extra_24_48, extra_24, extra_urgent, available,
          freeWeightLimit, shippingTypeRestrictions,
          weightPrices: { ...weightPrices, range: objectToArr(weightPrices.range) },
          priceRanges,
          deliveryDaysRanges,
          zoneWeightPrices: objectToArr(zoneWeightPrices).map(zwp => (zwp.zone = objectToArr(zwp.zone), zwp.range = objectToArr(zwp.range), zwp)),
          zoneFreeCases: objectToArr(zoneFreeCases).map(zfc => (zfc.zone = objectToArr(zfc.zone), zfc)),
          zoneShippingInfo: objectToArr(zoneShippingInfo).map(zsi => (zsi.zone = objectToArr(zsi.zone), zsi))
        };
        return shippingSettings as ShippingSettings;
      });
  }

  /**
   * Gets shipping balancer by zone list in real time.
   * @returns Observable with shipping balancer rows.
   */
  getShippingBalancerByZone$(): Observable<ShippingBalancerByZone[]> {
    return this.settingsDbService.getAll$<ShippingBalancerByZone>({
      subPath: 'shippingSettings/shippingBalancerByZone',
      converter: shippingBalancerByZoneFirestoreConverter
    });
  }

  /**
   * Creates or updates a settings document in `settings` collection.
   * @param uData Settings payload. If `id` exists, updates that document; otherwise creates a new one.
   * @returns Promise resolved with persisted document ID.
   */
  upsertSettings(uData: WithFieldValue<AdminSettings>): Promise<string> {
    return this.settingsDbService.upsert<AdminSettings>(uData);
  }

}
